"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Clock, PhoneCall, ShieldCheck, CircleCheck as CheckCircle2, Navigation, Car } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const areas = [{
  name: "Downtown Jonesboro",
  zip: "30236",
  eta: "15-25 min",
  details: "Historic District, Main St, Tara Blvd corridor"
}, {
  name: "Lake Spivey / Irondale",
  zip: "30236",
  eta: "15-30 min",
  details: "Spivey Hall area, residential lakefront properties"
}, {
  name: "Morrow & Southlake",
  zip: "30260",
  eta: "20-30 min",
  details: "Southlake Mall area, Mt Zion Rd, Clayton State vicinity"
}, {
  name: "Riverdale",
  zip: "30274",
  eta: "20-35 min",
  details: "Hwy 85 corridor, Upper Riverdale Rd, Valley Hill"
}, {
  name: "Stockbridge Border",
  zip: "30281",
  eta: "20-35 min",
  details: "Hudson Bridge Rd, Rock Quarry Rd, Clayton-Henry line"
}, {
  name: "Lovejoy & Bonanza",
  zip: "30250",
  eta: "20-35 min",
  details: "Tara Blvd South, McDonough Rd, Lovejoy community"
}];
const highlights = [{
  title: "Rapid Local Dispatch",
  desc: "Service trucks pre-stocked and stationed near major Jonesboro routes for fast response.",
  icon: Car
}, {
  title: "24/7 Always Open",
  desc: "Plumbers available day, night, weekends, and holidays across all covered zones.",
  icon: Clock
}, {
  title: "Licensed & Insured",
  desc: "Georgia-experienced plumbers protecting your home and business.",
  icon: ShieldCheck
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="servicearea" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs uppercase tracking-wide">
            <MapPin className="size-3.5 text-primary mr-1" />
            Clayton County & South Metro Atlanta
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Emergency Plumbing Coverage in Jonesboro, GA
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            When a pipe bursts, a drain backs up, or your water heater fails, minutes count. Our
            licensed team is stationed locally for immediate response throughout Jonesboro and adjacent
            communities.
          </p>
        </div>

        {/* Areas Grid & Map Highlight Box */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          {/* Areas Grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {areas.map((area, i) => <motion.div key={area.name} data-index={i} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: i * 0.05
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40">
                  <CardContent className="p-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <h3 className="font-default font-semibold text-base sm:text-lg text-card-foreground">
                          {area.name}
                        </h3>
                        <span className="text-xs font-mono font-medium px-2 py-0.5 rounded-sm bg-muted text-muted-foreground">
                          {area.zip}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground font-sans mb-4">
                        {area.details}
                      </p>
                    </div>
                    <div className="pt-3 border-t border-border/60 flex items-center justify-between text-xs font-medium text-foreground">
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Clock className="size-3.5 text-primary" />
                        Avg. Response
                      </span>
                      <span className="text-primary font-semibold">{area.eta}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          {/* Service Area Info Card */}
          <div className="lg:col-span-5">
            <Card className="border border-border bg-card shadow-md p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Navigation className="size-5" />
                </div>
                <div>
                  <h3 className="font-default text-lg sm:text-xl font-bold text-card-foreground">
                    Jonesboro Service Hub
                  </h3>
                  <p className="text-xs text-muted-foreground font-sans">
                    24-Hour Urgent Response Dispatch
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
                We maintain active service units across Tara Boulevard, Highway 19/41, and Highway 138 to
                ensure no customer waits in standing water or without running plumbing.
              </p>

              <div className="space-y-3 mb-8">
                <div className="flex items-start gap-2.5 text-xs sm:text-sm text-card-foreground">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>No extra travel charge for urgent Clayton County service</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs sm:text-sm text-card-foreground">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Fully equipped service vans with diagnostic cameras & parts</span>
                </div>
                <div className="flex items-start gap-2.5 text-xs sm:text-sm text-card-foreground">
                  <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                  <span>Straightforward pricing quoted prior to starting any repairs</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="w-full font-semibold shadow-sm">
                  <Link to="/contact/">
                    <PhoneCall className="size-4 mr-1.5" />
                    Request Urgent Service
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full">
                  <Link to="/services/">
                    View All Services
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </div>

        {/* Feature Highlights Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-border">
          {highlights.map((item, i) => {
          const Icon = item.icon;
          return <div key={item.title} data-index={i} className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 border border-border/40">
                <div className="size-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <Icon className="size-5" />
                </div>
                <div>
                  <h4 className="font-default font-semibold text-sm sm:text-base text-foreground mb-1">
                    {item.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-snug">
                    {item.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}